
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import useCurrency from "@/hooks/useCurrency"
import usePrivacyMode from "@/hooks/usePrivacyMode"
import { convertCurrency } from "@/services/ConvertCurrency"
import { displayOnlySignificatDigits, formatValue } from "@/services/FormatValue"
import { PortfolioItem } from "@/store/modules/auth/models/UserAccount"
import { IonSkeletonText } from '@ionic/vue';
import { computed, defineComponent, PropType, ref, Ref } from "vue"
import Asset from "@/store/modules/assets/models/Asset"

export default defineComponent({
    name: "AssetsList",
    components: { IonSkeletonText },
    props: {
        assets: {
            type: Array as PropType<Asset[]>,
            required: true
        },
        walletMode: {
            type: Boolean,
            required: true
        },
        routableAssets: {
            type: Boolean,
            required: true
        },
        searchQuery: {
            type: String,
            required: false
        },
        allowHistory: {
            type: Boolean,
            required: false
        },
        selectedAssetSymbol: {
            type: String,
            required: false
        }
    },
    emits: ['selectedAsset'],
    setup(props, { emit }) {
        const store = useStore()
        const router = useRouter()
        const isLoading = computed(() => store.getters.isLoading)
        const { preferredCurrency, currencyRate, baseCurrencyRate } = useCurrency()

        const portfolio = computed(() => store.getters.user.account.portfolio)
        const formatChange = (value: number) => (value > 0 ? '+' : '') + value.toFixed(2)

        const ownedVolume = (asset: string, portfolioAssets: PortfolioItem[]) => portfolioAssets.find(portfolioAsset => portfolioAsset.symbol === asset)?.quantity
        const searchQuery = computed(() => props.searchQuery ? props.searchQuery.toLowerCase() : "")
        
        const filteredAssets: Ref<Asset[]> = computed(() => props.assets.filter(asset => asset.symbol.toLowerCase().indexOf(searchQuery.value) > -1 || asset.name.toLowerCase().indexOf(searchQuery.value) > -1))
        const SKELETON_ITEMS = filteredAssets.value.length > 1 ? 5 : 1

        const currentAssetPrice = (asset: Asset) => formatValue(convertCurrency((asset.current_price * (ownedVolume(asset.symbol, portfolio.value) as number)), baseCurrencyRate.value, currencyRate.value), 6)

        const { PRIVACY_MASK, isPrivacyModeActive } = usePrivacyMode()

        const selectedAsset = ref(props.selectedAssetSymbol)

        const selectAsset = (assetSymbol: string) => {
            if (props.routableAssets) {
                router.push(`/tabs/asset/${assetSymbol}${props.allowHistory ? '/history' : ''}`)
            } else {
                selectedAsset.value = assetSymbol
                emit('selectedAsset', selectedAsset.value)
            }
        }
        
        return { isLoading, SKELETON_ITEMS, preferredCurrency, formatChange, portfolio, ownedVolume, filteredAssets, currencyRate, convertCurrency, displayOnlySignificatDigits, baseCurrencyRate, currentAssetPrice, formatValue, PRIVACY_MASK, isPrivacyModeActive, selectAsset, selectedAsset }
    },
})
